/**
 * Record extras (E4, SA-397, spec §9.3–9.4): quote versions, external
 * references, the record's source and the commercial status override.
 *
 * React Query, like the record's process panel — the Orders page's own lane
 * (`useOrders`) stays hand-rolled, so a write that can move the ORDER itself
 * (a sent quote mirrors the amount, a response or an override moves the
 * commercial status) is reported through the card's `onChanged`, and the page
 * refetches the detail. Wire shapes are snake_case; everything here returns
 * the camelCase types in `types/commerce`.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { ApiError, apiClient } from '@/services/apiClient'
import { EMAIL_DRAFT_KEY } from '@/hooks/useEmailDraft'
import { DRAFT_REFUSALS } from '@/components/records/quoteText'
import type {
  ExternalReference, OrderEventItem, QuoteRefusal, QuoteStep, QuoteVersion, QuoteVersions, WaitingResponse, RecordSource, RecordStatusMap,
  ReferenceSystem,
} from '@/types/commerce'

const BASE = '/api/commerce/orders'
export const recordsKey = ['records'] as const

type Wire = Record<string, unknown>

function toQuoteVersion(q: Wire): QuoteVersion {
  const response = q.response as Wire | null | undefined
  return {
    id: q.id as string,
    versionNumber: q.version_number as number,
    amountCents: q.amount_cents as number,
    currency: q.currency as string,
    validityHours: q.validity_hours as number,
    validUntil: q.valid_until as string,
    terms: (q.terms as string) ?? null,
    preparedAt: (q.prepared_at as string) ?? null,
    preparedByName: (q.prepared_by_name as string) ?? null,
    sentAt: (q.sent_at as string) ?? null,
    sentByName: (q.sent_by_name as string) ?? null,
    sentVia: (q.sent_via as 'email' | 'manual') ?? null,
    status: q.status as string,
    response: response ? {
      kind: response.kind as string,
      amountCents: (response.amount_cents as number) ?? null,
      note: (response.note as string) ?? null,
      recordedAt: response.recorded_at as string,
    } : null,
    document: q.document ? { name: (q.document as Wire).name as string, byteSize: (q.document as Wire).byte_size as number } : null,
    draft: q.draft ? {
      id: (q.draft as Wire).id as string, status: (q.draft as Wire).status as string,
      conversationId: (q.draft as Wire).conversation_id as string, sentAt: ((q.draft as Wire).sent_at as string) ?? null,
      dispatchedAt: ((q.draft as Wire).dispatched_at as string) ?? null,
      edited: Boolean((q.draft as Wire).edited),
    } : null,
    expired: Boolean(q.expired),
  }
}

function toQuoteStep(s: Wire): QuoteStep {
  return { id: s.id as string, number: s.number as number, title: s.title as string }
}

function toQuoteRefusal(raw: unknown): QuoteRefusal | null {
  const refusal = raw as Wire | null | undefined
  return refusal ? {
    code: refusal.code as string,
    currentStep: toQuoteStep(refusal.current_step as Wire),
    waitingSteps: ((refusal.waiting_steps as Wire[]) ?? []).map(toQuoteStep),
  } : null
}

function toWaitingResponse(w: Wire): WaitingResponse {
  return {
    id: w.id as string,
    intent: w.intent as string,
    confidence: (w.confidence as number | null) ?? null,
    channel: w.channel as string,
    fromName: (w.from_name as string | null) ?? null,
    receivedAt: w.received_at as string,
    conversationId: (w.conversation_id as string | null) ?? null,
    messageId: (w.message_id as string | null) ?? null,
    excerpt: (w.excerpt as string | null) ?? null,
    decidedByName: (w.decided_by_name as string | null) ?? null,
  }
}

function toQuoteVersions(raw: Wire): QuoteVersions {
  return {
    versions: ((raw.versions as Wire[]) ?? []).map(toQuoteVersion),
    current: raw.current ? toQuoteVersion(raw.current as Wire) : null,
    sendRefusal: toQuoteRefusal(raw.send_refusal),
    respondRefusal: toQuoteRefusal(raw.respond_refusal),
    prepared: raw.prepared ? toQuoteVersion(raw.prepared as Wire) : null,
    suggestedAmountCents: (raw.suggested_amount_cents as number) ?? null,
    draftRefusal: (raw.draft_refusal as string) ?? null,
    waitingResponses: ((raw.waiting_responses as Wire[]) ?? []).map(toWaitingResponse),
  }
}

function toReference(r: Wire): ExternalReference {
  return {
    id: r.id as string,
    system: r.system as string,
    reference: r.reference as string,
    url: (r.url as string) ?? null,
    label: (r.label as string) ?? null,
    createdByName: (r.created_by_name as string) ?? null,
    createdAt: (r.created_at as string) ?? null,
  }
}

const SOURCE_KINDS = new Set<string>(['email', 'whatsapp-group', 'conversation', 'none'])

function toSource(raw: Wire): RecordSource {
  const email = raw.email as Wire | null | undefined
  const group = raw.group as Wire | null | undefined
  const conversationId = (raw.conversation_id as string) ?? null
  // SA-946: a kind this client does not know is still a thread when there is
  // one — `none` alone would make the strip say "Entered by hand".
  const kind = typeof raw.kind === 'string' && SOURCE_KINDS.has(raw.kind)
    ? (raw.kind as RecordSource['kind'])
    : conversationId ? 'conversation' : 'none'
  return {
    kind,
    conversationId,
    email: email ? {
      mailboxId: email.mailbox_id as string,
      provider: (email.provider as string) ?? null,
      subject: (email.subject as string) ?? null,
      fromAddr: (email.from_addr as string) ?? null,
      receivedAt: (email.received_at as string) ?? null,
      webUrl: (email.web_url as string) ?? null,
      approvedByName: (email.approved_by_name as string) ?? null,
      ...(typeof email.message_id === 'string' ? { messageId: email.message_id } : {}),
    } : null,
    ...(group ? { group: {
      mailboxId: (group.mailbox_id as string) ?? null,
      messageId: (group.message_id as string) ?? null,
      subject: (group.subject as string) ?? null,
      fromName: (group.from_name as string) ?? null,
      receivedAt: (group.received_at as string) ?? null,
      approvedByName: (group.approved_by_name as string) ?? null,
    } } : {}),
  }
}

/** Words for a refused write. The record lane refuses with a CODE (409) or
 * an authored sentence (400); a code this page does not know is never
 * printed raw. */
const REFUSALS: Record<string, string> = {
  ...DRAFT_REFUSALS,
  draft_in_flight: 'The offer email is being sent. Wait for it, or retry it from the Inbox.',
  already_emailed: 'The offer email for this version went out: record it as emailed, then requote if it needs changing.',
  draft_exists: 'An email draft for this version is open. Edit or discard it in the Inbox.',
  draft_failed: 'The email could not be drafted. Try again, or download the quotation and send it your own way.',
  quote_version_not_current: 'That version is no longer the current one. Reload and try again.',
  quote_response_recorded: 'A response is already recorded on that version.',
  quote_version_unknown: 'That version no longer exists. Reload and try again.',
  quote_amount_invalid: 'Enter an amount greater than zero.',
  quote_validity_invalid: 'Enter how long the quote is valid, in hours.',
  quote_not_awaited: 'The process is not waiting for a quote now. Reload to see where it stands.',
  response_not_awaited: 'The process is not waiting for the customer’s response now. Reload to see where it stands.',
  quote_response_invalid: 'A counter needs an amount.',
  record_closed: 'This record is closed.',
  record_not_quote: 'Quote versions are for quote records only.',
  not_waiting: 'This update was already applied or dismissed.',
  process_closed: "The record's process is closed.",
  source_unavailable: 'The message is no longer stored, so it cannot be extracted.',
  nothing_to_apply: 'Take at least one field, or dismiss the update.',
  already_prepared: 'A prepared version already stands. Edit it or mark it as sent.',
  not_prepared: 'That version is no longer prepared. Reload to see where it stands.',
  quote_validity_passed: 'Its validity has passed. Edit the quote before sending it.',
  sent_via_invalid: 'That could not be saved.',
  external_reference_exists: 'This record already has that reference.',
}

export function recordErrorMessage(err: unknown): string {
  if (err instanceof ApiError && typeof err.detail === 'string') {
    if (REFUSALS[err.detail]) return REFUSALS[err.detail]
    return /^[a-z0-9_]+$/.test(err.detail) ? 'That could not be saved.' : err.detail
  }
  if (err instanceof ApiError && Array.isArray(err.detail)) {
    const first = err.detail[0] as { msg?: string } | undefined
    if (first?.msg) return first.msg
  }
  return err instanceof Error ? err.message : String(err)
}

// ── quote versions ─────────────────────────────────────────────────────────

export function useQuoteVersions(orderId: string | undefined): UseQueryResult<QuoteVersions, Error> {
  return useQuery({
    queryKey: [...recordsKey, orderId ?? '', 'quotes'],
    enabled: !!orderId,
    // SA-1003: `sendRefusal` follows the process, which a task page (a
    // Requote, a re-priced counter) moves without touching this key; the app
    // default (five minutes) would keep a stale form or a stale line.
    staleTime: 0,
    queryFn: async () => toQuoteVersions(await apiClient.get<Wire>(`${BASE}/${orderId}/quotes`)),
  })
}

export function usePrepareQuote(orderId: string) {
  const qc = useQueryClient()
  return useMutation<QuoteVersions, Error, { amountCents: number; validityHours: number; terms: string | null }>({
    mutationFn: async (p) => toQuoteVersions(await apiClient.post<Wire>(`${BASE}/${orderId}/quotes`, {
      amount_cents: p.amountCents, validity_hours: p.validityHours, terms: p.terms,
    })),
    onSuccess: (data) => {
      qc.setQueryData([...recordsKey, orderId, 'quotes'], data)
      void qc.invalidateQueries({ queryKey: EMAIL_DRAFT_KEY })
    },
    // SA-1003: a refusal usually means the process moved under us: show the truth.
    onError: () => { void qc.invalidateQueries({ queryKey: [...recordsKey, orderId, 'quotes'] }) },
  })
}

export function useUpdatePreparedQuote(orderId: string) {
  const qc = useQueryClient()
  return useMutation<QuoteVersions, Error, { versionNumber: number; amountCents: number; validityHours: number; terms: string | null }>({
    mutationFn: async (p) => toQuoteVersions(await apiClient.put<Wire>(`${BASE}/${orderId}/quotes/${p.versionNumber}`, {
      amount_cents: p.amountCents, validity_hours: p.validityHours, terms: p.terms,
    })),
    onSuccess: (data) => {
      qc.setQueryData([...recordsKey, orderId, 'quotes'], data)
      void qc.invalidateQueries({ queryKey: EMAIL_DRAFT_KEY })
    },
    onError: () => { void qc.invalidateQueries({ queryKey: [...recordsKey, orderId, 'quotes'] }) },
  })
}

export function useMarkQuoteSent(orderId: string) {
  const qc = useQueryClient()
  return useMutation<QuoteVersions, Error, { versionNumber: number; note: string | null }>({
    mutationFn: async (p) => toQuoteVersions(await apiClient.post<Wire>(`${BASE}/${orderId}/quotes/${p.versionNumber}/sent`, { note: p.note })),
    onSuccess: (data) => {
      qc.setQueryData([...recordsKey, orderId, 'quotes'], data)
      void qc.invalidateQueries({ queryKey: EMAIL_DRAFT_KEY })
    },
    onError: () => { void qc.invalidateQueries({ queryKey: [...recordsKey, orderId, 'quotes'] }) },
  })
}

/** SA-1040 PR 2 (§5.2): draft the offer email again for a prepared version. */
export function useRedraftQuote(orderId: string) {
  const qc = useQueryClient()
  return useMutation<QuoteVersions, Error, { versionNumber: number }>({
    mutationFn: async (p) => toQuoteVersions(await apiClient.post<Wire>(`${BASE}/${orderId}/quotes/${p.versionNumber}/draft`, {})),
    onSuccess: (data) => {
      qc.setQueryData([...recordsKey, orderId, 'quotes'], data)
      void qc.invalidateQueries({ queryKey: EMAIL_DRAFT_KEY })
    },
    onError: () => { void qc.invalidateQueries({ queryKey: [...recordsKey, orderId, 'quotes'] }) },
  })
}

/** How long a downloaded quotation's object URL lives. Revoked before the browser opens the
 *  download channel, the download is dropped (Safari, Firefox); FileSaver waits 40 s, this a
 *  minute (the route studies download's rule). */
export const QUOTE_DOWNLOAD_URL_LIFETIME_MS = 60_000

/** The rendered quotation through the api client (a bare href cannot carry the Bearer token;
 *  the conversations export's shape). Throws an ApiError on a refusal. */
export async function fetchQuoteDocument(orderId: string, versionNumber: number): Promise<Blob> {
  const res = await apiClient.fetch(`${BASE}/${orderId}/quotes/${versionNumber}/document`)
  if (!res.ok) throw new ApiError(`GET quote document failed`, res.status, await res.json().then((b) => b?.detail).catch(() => null))
  return res.blob()
}

/** The quotation saved to disk through a blob. Throws an ApiError on a refusal. */
export async function downloadQuoteDocument(orderId: string, versionNumber: number, name: string): Promise<void> {
  const url = URL.createObjectURL(await fetchQuoteDocument(orderId, versionNumber))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), QUOTE_DOWNLOAD_URL_LIFETIME_MS)
}

export function useRecordQuoteResponse(orderId: string) {
  const qc = useQueryClient()
  return useMutation<QuoteVersions, Error, {
    versionNumber: number; kind: 'counter' | 'accepted' | 'declined'; amountCents: number | null; note: string | null
  }>({
    mutationFn: async (p) => toQuoteVersions(await apiClient.post<Wire>(
      `${BASE}/${orderId}/quotes/${p.versionNumber}/response`,
      { kind: p.kind, amount_cents: p.amountCents, note: p.note },
    )),
    onSuccess: (data) => qc.setQueryData([...recordsKey, orderId, 'quotes'], data),
    // A refusal usually means the versions moved under us: show the truth.
    onError: () => { void qc.invalidateQueries({ queryKey: [...recordsKey, orderId, 'quotes'] }) },
  })
}

/** SA-1047: dismiss a waiting customer update (a response read from an approved reply), with an optional reason. */
export function useDismissCustomerUpdate(orderId: string) {
  const qc = useQueryClient()
  const refetch = () => { void qc.invalidateQueries({ queryKey: [...recordsKey, orderId, 'quotes'] }) }
  return useMutation<unknown, Error, { updateId: string; reason?: string }>({
    mutationFn: (p) => apiClient.post(
      `${BASE}/${orderId}/customer-updates/${p.updateId}/dismiss`,
      { reason: p.reason?.trim() ? p.reason.trim() : null },
    ),
    onSettled: refetch,
  })
}

// ── external references ────────────────────────────────────────────────────

export function useReferences(orderId: string | undefined): UseQueryResult<ExternalReference[], Error> {
  return useQuery({
    queryKey: [...recordsKey, orderId ?? '', 'references'],
    enabled: !!orderId,
    queryFn: async () => (await apiClient.get<Wire[]>(`${BASE}/${orderId}/references`)).map(toReference),
  })
}

export function useReferenceSystems(): UseQueryResult<ReferenceSystem[], Error> {
  return useQuery({
    queryKey: [...recordsKey, 'reference-systems'],
    queryFn: () => apiClient.get<ReferenceSystem[]>(`${BASE}/reference-systems`),
    staleTime: 5 * 60_000,
  })
}

export function useAddReference(orderId: string) {
  const qc = useQueryClient()
  return useMutation<ExternalReference, Error, { system: string; reference: string; url: string; label: string }>({
    mutationFn: async (p) => toReference(await apiClient.post<Wire>(`${BASE}/${orderId}/references`, {
      system: p.system.trim(), reference: p.reference.trim(),
      url: p.url.trim() || null, label: p.label.trim() || null,
    })),
    onSettled: () => { void qc.invalidateQueries({ queryKey: [...recordsKey, orderId, 'references'] }) },
  })
}

export function useRemoveReference(orderId: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (refId) => apiClient.delete(`${BASE}/${orderId}/references/${refId}`),
    onSettled: () => { void qc.invalidateQueries({ queryKey: [...recordsKey, orderId, 'references'] }) },
  })
}

// ── source ─────────────────────────────────────────────────────────────────

export function useRecordSource(orderId: string | undefined): UseQueryResult<RecordSource, Error> {
  return useQuery({
    queryKey: [...recordsKey, orderId ?? '', 'source'],
    enabled: !!orderId,
    queryFn: async () => toSource(await apiClient.get<Wire>(`${BASE}/${orderId}/source`)),
    staleTime: 5 * 60_000,   // where a record came from does not change
  })
}

// ── ledger events ──────────────────────────────────────────────────────────

/** `GET /orders/{id}/events` row → the page's shape (the same mapping as
 * `useOrders.transformOrderEvent`, which the hand-rolled lane keeps). */
function toEvent(e: Wire): OrderEventItem {
  return {
    id: e.id as string,
    eventType: e.event_type as string,
    payload: (e.payload as Record<string, unknown>) ?? null,
    actorType: e.actor_type as string,
    actorId: (e.actor_id as string) ?? null,
    occurredAt: e.occurred_at as string,
  }
}

/**
 * The record's ledger, newest first as served (SA-460, spec §6.3): the step
 * drawer's per-step lines and, from SA-461, the Activity section read this
 * ONE query. `limit=500` is the endpoint's cap — the default 100 would drop
 * an early step's rows on a busy record. `staleTime: 0` because a task
 * completed on the task page moves the ledger while invalidating only
 * `['process']` (the `usePricing` precedent).
 */
export function useRecordEvents(orderId: string | undefined): UseQueryResult<OrderEventItem[], Error> {
  return useQuery({
    queryKey: [...recordsKey, orderId ?? '', 'events'],
    enabled: !!orderId,
    queryFn: async () => ((await apiClient.get<{ items?: Wire[] }>(`${BASE}/${orderId}/events?limit=500`)).items ?? []).map(toEvent),
    staleTime: 0,
  })
}

// ── commercial status ──────────────────────────────────────────────────────

/** With an id: the map that resolves for THAT record (what the override PUT
 * accepts). Without: every status the deployment's pack names (the filter). */
export function useStatusMap(orderId?: string, enabled = true): UseQueryResult<RecordStatusMap, Error> {
  return useQuery({
    queryKey: [...recordsKey, orderId ?? '', 'status-map'],
    enabled,
    queryFn: () => apiClient.get<RecordStatusMap>(
      orderId ? `${BASE}/status-map?order_id=${orderId}` : `${BASE}/status-map`),
    staleTime: 60_000,
  })
}

export function useSetCommercialStatus(orderId: string) {
  return useMutation<void, Error, { override: string | null; note: string | null }>({
    mutationFn: async (p) => { await apiClient.put(`${BASE}/${orderId}/commercial-status`, p) },
  })
}
